import type {
  AcpSubagentData,
  AcpSubagentToolData,
  CrowdexUIMessage,
  CrowdexUIMessagePart,
} from '@crowdex/agent-contracts';
import { sanitizeSubagentTranscript } from '@crowdex/agent-contracts';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@crowdex/ui/ai/conversation';
import { Check, LoaderCircle, XCircle } from 'lucide-react';
import { DesktopMessageList } from '@/components/desktop-message-list';
import { OriginCard, ParentLink } from '@/components/desktop-shell/child-conversation';
import { useOptionalDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';

function toolPart(tool: AcpSubagentToolData): CrowdexUIMessagePart {
  const head = {
    type: 'dynamic-tool' as const,
    toolCallId: tool.toolCallId,
    toolName: tool.kind ?? 'other',
    title: tool.title,
    input: tool.input,
  };

  if (tool.status === 'completed') {
    return { ...head, state: 'output-available', output: tool.output };
  }
  if (tool.status === 'failed' || tool.status === 'cancelled') {
    return {
      ...head,
      state: 'output-error',
      errorText:
        typeof tool.output === 'string'
          ? tool.output
          : tool.status === 'cancelled'
            ? 'Sub-agent tool cancelled.'
            : 'Sub-agent tool failed.',
    };
  }
  return { ...head, state: 'input-available' };
}

function conversationMessages(data: AcpSubagentData): CrowdexUIMessage[] {
  const assistantParts: CrowdexUIMessagePart[] = [];
  const transcript = sanitizeSubagentTranscript(data.transcript);
  if (data.thought.trim().length > 0) {
    assistantParts.push({
      type: 'reasoning',
      text: data.thought,
      state: data.status === 'pending' || data.status === 'in_progress' ? 'streaming' : 'done',
    });
  }
  assistantParts.push(...data.tools.map(toolPart));
  if (transcript.length > 0) {
    assistantParts.push({ type: 'text', text: transcript });
  } else if (data.status === 'pending' || data.status === 'in_progress') {
    assistantParts.push({ type: 'text', text: data.activity ?? 'Waiting for activity…' });
  }

  return [
    ...(data.prompt === null || data.prompt.trim().length === 0
      ? []
      : [
          {
            id: `${data.toolCallId}-prompt`,
            role: 'user' as const,
            parts: [{ type: 'text' as const, text: data.prompt }],
          },
        ]),
    {
      id: `${data.toolCallId}-response`,
      role: 'assistant',
      parts: assistantParts,
    },
  ];
}

/**
 * A subagent as a child conversation (design D49, mobile 47): the way back to the conversation
 * that started it, its title and state, the task it was given as its first message, then its
 * work (its steps folded) and its answer. It works inside its parent's turn: what it is told
 * goes through the parent.
 */
export function DesktopSubagentConversation({
  data,
  parentTitle,
  onOpenParent,
}: {
  data: AcpSubagentData;
  /** The conversation that started it. */
  parentTitle?: string;
  onOpenParent?: () => void;
}) {
  const running = data.status === 'pending' || data.status === 'in_progress';
  const failed = data.status === 'failed' || data.status === 'cancelled';
  const StatusIcon = running ? LoaderCircle : failed ? XCircle : Check;
  const [prompt, ...work] = conversationMessages(data);
  const origin = prompt?.role === 'user' ? prompt : undefined;
  const store = useOptionalDesktopShellStoreApi();
  const parent =
    parentTitle ?? store?.getState().generatedConversationTitle ?? 'Parent conversation';

  return (
    <div className="flex h-full min-h-0 flex-col" data-child-conversation>
      <Conversation className="min-h-0 flex-1">
        <ConversationContent className="mx-auto min-h-full w-full max-w-conversation gap-5 px-5 py-6 sm:px-7">
          <header className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <ParentLink
                child={{ parentId: '', parentTitle: parent, parentColor: null, task: null }}
                {...(onOpenParent === undefined ? {} : { onOpen: onOpenParent })}
              />
              <h2 className="type-card-title truncate">{data.title}</h2>
            </div>
            <span
              className={`type-meta flex h-7 shrink-0 items-center gap-1.5 rounded-control px-2.5 ${
                running ? 'bg-running/15 text-foreground' : 'bg-control text-muted-foreground'
              }`}
            >
              <StatusIcon
                className={`size-3.5 ${running ? 'animate-spin text-running' : failed ? 'text-destructive' : ''}`}
              />
              {running ? 'Running' : failed ? 'Failed' : 'Done'}
            </span>
          </header>
          {origin !== undefined && (
            <OriginCard child={{ parentTitle: parent, task: null }}>
              <p className="whitespace-pre-wrap">{data.prompt}</p>
            </OriginCard>
          )}
          <DesktopMessageList
            messages={
              origin === undefined
                ? [prompt, ...work].filter((message) => message !== undefined)
                : work
            }
          />
        </ConversationContent>
        <ConversationScrollButton className="bottom-3 size-8 border-border bg-card shadow-lg" />
      </Conversation>
    </div>
  );
}
